import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { Box, Grid, Paper, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { isEmpty } from 'lodash-es';
import { FC, useEffect, useState } from 'react';
import FileViewer from 'react-file-viewer';
import { LuPencil } from 'react-icons/lu';
import { useSearchParams } from 'react-router-dom';
import { IJob, getCandidateResumeByFileUrl, getPresignedURLForGet, amplitude } from 'wisestep-api';
import {
  Item,
  WsButton,
  WsColors,
  WsToggleButton,
  WsTypography,
  getSearchRegExp,
} from 'wisestep-base';

import useAdjustHeightOnZoom from '../../../../../features/candidateLayout/hooks/useAdjustHeightOnZoom';
import getDecodedQueryParams from '../../../../../utils/getDecodedQueryParams';
import { filterS3Url, highlightKeys, trackCV, downloadCV } from '../../../utils/cvHelper';
import UploadFile from '../../documents/components/UploadFile';

import { UseGetHighlightCVContent } from './Hooks/UseGetHighlightCVContent';
import PdfViewer from './PdfViewer';

const IFrame = styled('iframe')(() => ({
  height: '100%',
  width: '100%',
  border: 0,
  overflowY: 'auto',
}));

interface ICandidateResumeView {
  resumeLocation?: string;
  pdfResumeUrl?: string;
  job: IJob | null;
  resumeFileName?: string;
  componentHeight?: string;
  showUpload?: boolean;
}

const CandidateResume: FC<ICandidateResumeView> = ({
  resumeLocation,
  job,
  resumeFileName,
  showUpload = false,
}) => {
  const [isResumeLoaded, setIsResumeLoaded] = useState(false);
  const [url, setUrl] = useState<{ url: string; type: string; resumeContent?: string } | null>(
    null
  );
  const [isOriginaFormatView, setIsOriginaFormatView] = useState(true);
  const zoomLevel = useAdjustHeightOnZoom();
  const [searchParams] = useSearchParams();
  const [openUpload, setOpenUpload] = useState<boolean>(false);

  const searchQuery =
    (searchParams.get('q') &&
      decodeURI(getDecodedQueryParams(searchParams.get('q') ?? '')?.['query'] ?? '')) ??
    '';

  const [regex] = useState<RegExp | undefined>(getSearchRegExp(searchQuery));
  const highLightedContent = UseGetHighlightCVContent(searchQuery, url?.url ?? '');

  useEffect(() => {
    const getHighLightKeyWordResume = async (url: string) => {
      if (searchQuery && !isOriginaFormatView) {
        try {
          const response = await getCandidateResumeByFileUrl(url);
          if (
            response.data.content &&
            response.data.content !== 'content not found' &&
            response.data.content !== 'NO CONTENT' &&
            response.data.content !== 'NO_CONTENT' &&
            response.data.content !== 'EXTRACTION_FAILED'
          ) {
            return {
              url: response.data.resumeLocation ? response.data.resumeLocation : '',
              type: 'content',
              resumeContent: response.data.content.replace(/\n/g, '<br>'),
            };
          }
          return await filterS3Url(url);
        } catch (error) {
          return await filterS3Url(url);
        }
      }
      return await filterS3Url(url);
    };
    (async () => {
      if (!isEmpty(resumeLocation)) {
        setIsResumeLoaded(false);
        const getURL = await getHighLightKeyWordResume(resumeLocation ?? '');
        setUrl(getURL);
        setIsResumeLoaded(true);
        job == null ? trackCV('view', resumeLocation) : trackCV('view', resumeLocation, job);
      } else {
        setIsResumeLoaded(true);
      }
    })();
  }, [job, resumeLocation, isOriginaFormatView]);
  const [resumeUrl, setResumeUrl] = useState('');
  useEffect(() => {
    const getUrl = async () => {
      const data = await getPresignedURLForGet({
        fileName: resumeLocation ?? '',
      });
      setResumeUrl(data.data.preSignedUrl);
    };
    getUrl();
  }, [resumeFileName, resumeLocation]);
  const CvDownload = () => {
    if (resumeUrl) {
      trackCV('download', resumeUrl);
      downloadCV(resumeUrl.split('?')[0], resumeFileName);
    }
  };
  return (
    <Paper
      sx={{
        borderColor: WsColors.TertiaryShades100,
        background: WsColors.White,
        marginLeft: 1,
        marginTop: -1,
        paddingTop: 1,
        height: `${(1 + (1 - zoomLevel)) * 38}rem`,
      }}
    >
      <Grid
        justifyContent="space-between"
        sx={{ background: WsColors.White }}
        mx={2}
        my={1}
        display="flex"
      >
        <WsToggleButton
          buttonsList={[
            { label: 'Original', value: 'original' },
            { label: 'Text', value: 'text' },
          ]}
          selectedIndexValue={() => setIsOriginaFormatView(!isOriginaFormatView)}
          defaultValue={'original'}
        />
        <Grid item>
          {showUpload && (
            <WsButton
              variant="text"
              startIcon={<LuPencil color={WsColors.TheBlues500} />}
              onClick={() => {
                setOpenUpload(true);
              }}
            >
              Upload
            </WsButton>
          )}
          <WsButton
            variant="text"
            startIcon={<FileDownloadOutlinedIcon sx={{ color: WsColors.TheBlues500 }} />}
            onClick={() => {
              CvDownload(),
                amplitude('CandidateView_CV_DownloadCV', {
                  clickedOn: 'Download CV',
                  pageName: 'candidate-layout',
                  pageUrl: window.location.href,
                });
            }}
          >
            Download
          </WsButton>
        </Grid>
      </Grid>
      <Box
        sx={{
          background: WsColors.TertiaryShades200,
          height: `${(1 + (1 - zoomLevel)) * 38 - 0.5}rem`,
        }}
      >
        <Item sx={{ height: '100%', bgcolor: WsColors.White, mr: 1, width: '100%', py: 0 }}>
          {isResumeLoaded ? (
            <>
              {url ? (
                isOriginaFormatView ? (
                  <>
                    {url.type == 'image' ? (
                      <img
                        id="imageviewer"
                        src={url.url}
                        style={{ width: '100%', height: '100%' }}
                        alt="CV View Error"
                      />
                    ) : resumeLocation?.split('.').pop() === 'pdf' ? (
                      <PdfViewer url={resumeUrl} highlightRegexp={regex} />
                    ) : resumeLocation?.split('.').pop() === 'doc' ? (
                      <IFrame src={url?.url + '#toolbar=0'} height={'100%'} />
                    ) : resumeUrl && resumeLocation?.split('.').pop() === 'docx' ? (
                      <FileViewer fileType="docx" filePath={resumeUrl} />
                    ) : resumeUrl && resumeLocation?.split('.').pop() === 'html' ? (
                      <Typography
                        height="100%"
                        sx={{ overflow: 'hidden', overflowY: 'scroll' }}
                        dangerouslySetInnerHTML={{
                          __html: highLightedContent ?? '',
                        }}
                      />
                    ) : (
                      <IFrame id="imageviewer" title="imageviewer" src={url.url} />
                    )}
                  </>
                ) : (
                  <>
                    {url.type == 'image' ? (
                      <img
                        id="imageviewer"
                        src={url.url}
                        style={{ width: '100%', height: '100%' }}
                        alt="CV View Error"
                      />
                    ) : url.type == 'content' ? (
                      <Box sx={{ height: '100vh', bgcolor: WsColors.White, marginRight: 1 }}>
                        <Typography
                          height="100%"
                          sx={{ overflow: 'hidden', overflowY: 'scroll' }}
                          dangerouslySetInnerHTML={{
                            __html: url.resumeContent
                              ? highlightKeys(
                                  url.resumeContent.replace(
                                    /<script[\s\S]*?>[\s\S]*?<\/script>/gi,
                                    'illegalscript'
                                  ),
                                  searchQuery ?? ''
                                )
                              : '',
                          }}
                        />
                      </Box>
                    ) : (
                      <IFrame id="imageviewer" title="imageviewer" src={url.url} />
                    )}
                  </>
                )
              ) : (
                <Grid justifyContent="center" alignItems="center" display="flex">
                  <WsTypography>
                    {resumeLocation && resumeLocation.indexOf('.doc') > -1
                      ? '.doc format file view are not supported in browser. Please download the file to view it locally'
                      : 'CV Not Available'}
                  </WsTypography>
                </Grid>
              )}
            </>
          ) : (
            <Grid justifyContent="center" textAlign="center" display="flex">
              <WsTypography>Loading CV...</WsTypography>
            </Grid>
          )}
        </Item>
      </Box>
      <UploadFile open={openUpload} setOpen={setOpenUpload} disableSelectCatagory={true} />
    </Paper>
  );
};

export default CandidateResume;
